<template>
  <div class="min-h-screen w-full h-full flex flex-col bg-zinc-950 text-zinc-400 font-light select-none text-left pl-6">
    
    <div class="p-4 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div class="flex items-center gap-4">
        <div class="space-y-0.5">
          <h2 class="text-sm font-mono uppercase tracking-widest text-white m-0">// Console Agendas & Plannings</h2>
          <p class="text-[10px] font-mono text-zinc-600 uppercase tracking-wider m-0">
            {{ currentPeriodLabel }}
          </p>
        </div>

        <!-- Navigation de dates -->
        <div class="flex items-center gap-2">
          <button @click="goToPreviousPeriod" class="p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-400 hover:text-white transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button @click="goToToday" class="px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-mono text-zinc-400 hover:text-white transition-colors uppercase tracking-wide">
            Aujourd'hui
          </button>
          <button @click="goToNextPeriod" class="p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-400 hover:text-white transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">Filtrer :</span>
        
        <button 
          type="button"
          @click="toggleAllEmployees"
          class="px-3 py-1.5 rounded-lg border text-[10px] font-mono uppercase tracking-wider transition-all focus:outline-none cursor-pointer"
          :class="selectedEmployeeIds.length === employees.length
            ? 'bg-white text-black font-semibold border-white' 
            : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-white'"
        >
          Vue Équipe
        </button>

        <button
          v-for="emp in employees"
          :key="emp.id"
          @click="toggleEmployeeFilter(emp.id)"
          class="px-3 py-1.5 rounded-lg border text-[10px] font-mono flex items-center gap-2 transition-all focus:outline-none cursor-pointer"
          :class="selectedEmployeeIds.includes(emp.id)
            ? 'bg-zinc-900 text-white border-zinc-700 font-medium' 
            : 'bg-zinc-950 text-zinc-600 border-zinc-900/60 hover:text-zinc-400'"
        >
          <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: emp.color || '#D5CCBC' }"></span>
          <span>{{ emp.name }}</span>
        </button>
      </div>
    </div>

    <div class="grid grid-cols-[65px_1fr] border-b border-zinc-900 sticky top-[73px] left-0 w-full bg-zinc-950 z-30 shrink-0">
      <div class="border-r border-zinc-900/60 bg-zinc-950"></div>
      
      <div class="grid" :class="gridColsClass">
        <div
          v-for="(col, index) in activeColumns"
          :key="index"
          class="p-3 flex flex-col items-center justify-center text-center gap-0.5 border-l border-zinc-900/40 first:border-l-0 bg-zinc-950"
        >
          <template v-if="viewMode === 'employee-week'">
            <span 
              class="text-[10px] uppercase tracking-widest font-medium"
              :class="{ 'text-[#D5CCBC]': isToday(col.date), 'text-zinc-500': !isToday(col.date) }"
            >
              {{ formatDateLabel(col.date, { weekday: "short" }) }}
            </span>
            <span 
              class="text-sm font-semibold tracking-tight font-mono"
              :class="{ 'text-white bg-zinc-900 px-2 py-0.5 rounded-lg border border-zinc-800': isToday(col.date), 'text-zinc-300': !isToday(col.date) }"
            >
              {{ formatDateLabel(col.date, { day: "numeric" }) }}
            </span>
          </template>

          <template v-else-if="viewMode === 'team-day'">
            <span class="text-[10px] uppercase tracking-widest font-bold text-white tracking-wide">
              {{ col.employeeName }}
            </span>
            <span class="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">
              {{ col.employeeRole || 'Collaborateur' }}
            </span>
          </template>
        </div>
      </div>
    </div>

    <div class="grid w-full overflow-y-auto max-h-[75vh] relative grid-cols-[65px_1fr] no-scrollbar">
      
      <div class="flex flex-col border-r border-zinc-900 bg-zinc-950/60 relative z-10">
        <div
          v-for="hour in hours"
          :key="`hour-label-${hour}`"
          class="h-[100px] sm:h-[120px] px-2 flex items-start justify-end text-[10px] text-zinc-600 font-medium font-mono pt-1.5 border-t border-zinc-900/60 first:border-t-0"
        >
          {{ formatHour(hour) }}
        </div>
      </div>

      <div class="grid relative bg-zinc-950/20" :class="gridColsClass">
        
        <div
          ref="nowIndicator"
          class="absolute left-0 right-0 h-[1px] bg-[#D5CCBC] z-20 flex items-center pointer-events-none"
          :style="nowIndicatorStyle"
        >
          <div class="absolute left-[-65px] bg-[#D5CCBC] text-[9px] font-mono font-semibold text-black px-1 py-0.5 rounded shadow-lg tracking-wide whitespace-nowrap">
            {{ nowLabel }}
          </div>
          <div class="w-1.5 h-1.5 rounded-full bg-[#D5CCBC] absolute left-0 -translate-x-1/2 shadow-md shadow-black"></div>
        </div>

        <div
          v-for="(col, colIndex) in activeColumns"
          :key="`col-${colIndex}`"
          class="relative border-l border-zinc-900/60 min-h-[600px] day-column first:border-l-0"
        >
          <div
            v-for="hour in hours"
            :key="`hour-${colIndex}-${hour}`"
            class="h-[100px] sm:h-[120px] p-1 border-t border-zinc-900/40 first:border-t-0 transition-colors duration-200 hover:bg-zinc-900/30 cursor-crosshair"
            @click="handleCalendarClick(colIndex, hour)"
          ></div>

          <div
            v-for="event in col.events"
            :key="`event-${colIndex}-${event.id}`"
            class="event-block resizable-event absolute rounded-xl p-2 border bg-zinc-900/90 backdrop-blur-md w-[94%] mx-auto left-0 right-0 cursor-pointer z-10 overflow-hidden shadow-xl shadow-black/40 hover:scale-[1.01] hover:border-zinc-700 transition-all duration-300"
            :data-id="event.id"
            :style="getEventStyle(event)"
            :class="[
              event.status === 'Validé' ? 'border-emerald-900/80 text-emerald-400' :
              event.status === 'En attente' ? 'border-amber-900/80 text-amber-400' : 'border-zinc-800 text-zinc-300'
            ]"
            @click="openSidebar(event)"
          >
            <div class="space-y-1 h-full flex flex-col justify-between text-left">
              <div class="min-w-0">
                <div class="flex items-center justify-between gap-2">
                  <p class="text-[11px] font-medium text-white truncate tracking-wide m-0">
                    {{ event.nom }}
                  </p>
                  <span 
                    v-if="viewMode === 'employee-week'"
                    class="w-1.5 h-1.5 rounded-full shrink-0 shadow-sm"
                    :style="{ backgroundColor: getEmployeeColor(event.employeeId) }"
                    :title="getEmployeeName(event.employeeId)"
                  ></span>
                </div>
                <p class="text-[10px] text-zinc-500 truncate font-light mt-0.5 m-0">
                  {{ event.service.map(s => s.nom).join(', ') }}
                </p>
              </div>
              
              <div class="flex flex-wrap items-center justify-between gap-1 mt-1 pt-1 border-t border-zinc-900/60 shrink-0">
                <span class="text-[10px] font-mono font-medium text-[#D5CCBC]">
                  {{ formatHourMinute(event.start) }}
                </span>
                <span 
                  class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md select-none truncate max-w-full font-mono shrink-0"
                  :class="[
                    event.status === 'Validé' ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-900/40' :
                    event.status === 'En attente' ? 'bg-amber-950/60 text-amber-400 border border-amber-900/40' : 'bg-zinc-900 text-zinc-400'
                  ]"
                >
                  {{ event.status }}
                </span>
              </div>
            </div>
            
            <div
              class="resize-handle w-full h-1.5 absolute bottom-0 left-0 cursor-ns-resize bg-zinc-800/80 hover:bg-[#D5CCBC] transition-colors rounded-b-xl"
              @mousedown.stop="startResize($event, event)"
            ></div>
          </div>
        </div>

        <div
          v-if="resizeTooltip.visible"
          class="absolute z-50 bg-zinc-900 border border-zinc-800 text-white font-mono text-[10px] px-2 py-1 rounded-lg shadow-2xl pointer-events-none transition-all duration-100"
          :style="{ top: `${resizeTooltip.y}px`, left: `${resizeTooltip.x}px` }"
        >
          {{ formatEventTime(resizeTooltip.previewEnd) }}
        </div>
      </div>
    </div>

    <EventSidebar
      :visible="showSidebar"
      :event="selectedEvent"
      @close="closeSidebar"
      @edit="handleEditAppointment"
      @cancel="handleCancelAppointment"
      @remind="handleSendReminder"
      @payment="handlePayment"
    />

  </div>
</template>

<script>
import api from '@/api'
import { toast } from 'vue3-toastify'
import EventSidebar from '@/components/EventSidebar.vue'

export default {
  name: 'AdminCalendarView',
  components: {
    EventSidebar
  },
  props: {
    // List of team employees available
    employees: {
      type: Array,
      default: () => []
    },
    // Raw calendar events dataset from API
    eventsByDay: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      hours: Array.from({ length: 13 }, (_, i) => i + 8), // 08:00 to 20:00
      selectedEmployeeIds: [],
      selectedEvent: null,
      showSidebar: false,
      resizeTooltip: { visible: false, x: 0, y: 0, previewEnd: '' },
      nowIndicatorStyle: { top: '35%' },
      nowLabel: '14:22',
      loadedEmployees: [],
      loadedEventsByDay: [],
      currentDate: new Date(),
      isResizing: false,
      resizingEvent: null,
      originalHeight: 0
    }
  },
  async mounted() {
    await this.fetchStaff()
    await this.fetchAppointments()
  },
  computed: {
    // Use loaded employees or props fallback
    activeEmployees() {
      return this.loadedEmployees.length > 0 ? this.loadedEmployees : this.employees
    },
    activeEventsByDay() {
      return this.loadedEventsByDay.length > 0 ? this.loadedEventsByDay : this.eventsByDay
    },
    currentPeriodLabel() {
      if (this.viewMode === 'employee-week') {
        // Show week range
        const startOfWeek = new Date(this.currentDate)
        startOfWeek.setDate(this.currentDate.getDate() - this.currentDate.getDay() + 1)
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)

        const options = { day: 'numeric', month: 'short', year: 'numeric' }
        return `Semaine du ${startOfWeek.toLocaleDateString('fr-FR', options)} au ${endOfWeek.toLocaleDateString('fr-FR', options)}`
      } else {
        // Show single day
        const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
        return this.currentDate.toLocaleDateString('fr-FR', options)
      }
    },
    // Detect operational layout engine mode
    viewMode() {
      // If only 1 employee is checked -> Week view for this worker
      return this.selectedEmployeeIds.length === 1 ? 'employee-week' : 'team-day';
    },

    // Compute columns layout safely depending on active filtration
    activeColumns() {
      if (this.viewMode === 'employee-week') {
        // Return standard multi-day array, but filtered for this single worker's tasks
        const targetEmpId = this.selectedEmployeeIds[0];
        return this.activeEventsByDay.map(day => ({
          date: day.date,
          events: day.events.filter(e => e.employeeId === targetEmpId)
        }));
      } else {
        // Multi-Employee view: Each column is an active employee for "Today"
        // (Assuming first element or designated selected active date)
        const activeDayData = this.activeEventsByDay[0] || { date: new Date(), events: [] };

        return this.activeEmployees
          .filter(emp => this.selectedEmployeeIds.includes(emp.id))
          .map(emp => ({
            employeeId: emp.id,
            employeeName: emp.name,
            employeeRole: emp.role,
            date: activeDayData.date,
            events: activeDayData.events.filter(e => e.employeeId === emp.id)
          }));
      }
    },

    // Calculates dynamic Tailwind grid columns class based on column array length
    gridColsClass() {
      const colCount = this.activeColumns.length;
      return {
        'grid-cols-1': colCount === 1,
        'grid-cols-2': colCount === 2,
        'grid-cols-3': colCount === 3,
        'grid-cols-4': colCount === 4,
        'grid-cols-5': colCount === 5,
        'grid-cols-6': colCount === 6,
        'grid-cols-7': colCount === 7,
      };
    }
  },
  methods: {
    toggleEmployeeFilter(id) {
      const idx = this.selectedEmployeeIds.indexOf(id);
      if (idx > -1) {
        // Prevent disabling last employee to keep grid alive
        if (this.selectedEmployeeIds.length > 1) {
          this.selectedEmployeeIds.splice(idx, 1);
        }
      } else {
        this.selectedEmployeeIds.push(id);
      }
    },
    toggleAllEmployees() {
      this.selectedEmployeeIds = this.activeEmployees.map(e => e.id);
    },
    isToday(dateStr) {
      return new Date(dateStr).toDateString() === new Date().toDateString();
    },
    formatDateLabel(dateStr, options) {
      return new Date(dateStr).toLocaleDateString("fr-FR", options);
    },
    getEmployeeColor(id) {
      const emp = this.activeEmployees.find(e => e.id === id);
      return emp ? emp.color : '#D5CCBC';
    },
    getEmployeeName(id) {
      const emp = this.activeEmployees.find(e => e.id === id);
      return emp ? emp.name : 'Inconnu';
    },
    async fetchStaff() {
      try {
        const response = await api.staff.getAllStaff()
        const staffList = response.data?.administrateurs || []

        this.loadedEmployees = staffList.map((staff, index) => ({
          id: staff._id,
          name: staff.nom,
          role: staff.specialite || 'Collaborateur',
          color: ['#e4e4e7', '#c4b5fd', '#fde047', '#fda4af', '#86efac'][index % 5]
        }))

        this.selectedEmployeeIds = this.loadedEmployees.map(e => e.id)
      } catch (error) {
        console.error('Erreur lors du chargement du personnel', error)
        toast.error('Erreur lors du chargement du personnel')
      }
    },
    async fetchAppointments() {
      try {
        const response = await api.appointments.getAllAppointments()
        const appointments = response.data || []

        // Group appointments by day based on currentDate
        const weekDays = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(this.currentDate)
          date.setDate(this.currentDate.getDate() - this.currentDate.getDay() + i + 1)
          return date
        })

        this.loadedEventsByDay = weekDays.map(date => {
          const dayEvents = appointments
            .filter(apt => {
              const aptDate = new Date(apt.start)
              return aptDate.toDateString() === date.toDateString()
            })
            .map(apt => {
              const startDate = new Date(apt.start)
              const endDate = new Date(apt.end)

              const startHour = startDate.getHours()
              const startMinute = startDate.getMinutes()
              const endHour = endDate.getHours()
              const endMinute = endDate.getMinutes()

              const topOffset = ((startHour - 8) * 100) + (startMinute / 60 * 100)
              const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute)
              const cardHeight = (duration / 60) * 100

              return {
                id: apt.id || apt._id,
                employeeId: apt.employe?.id || apt.employe?._id || null,
                nom: apt.nom,
                telephone: apt.telephone || '',
                email: apt.email || '',
                clientId: apt.clientId || null,
                service: apt.service || [],
                start: startDate,
                end: endDate,
                status: apt.status || 'En attente',
                employe: apt.employe || {},
                paiement: apt.paiement || 'Non payé',
                notes: apt.notes || '',
                topOffset: Math.max(0, topOffset),
                cardHeight: Math.max(50, cardHeight)
              }
            })

          return {
            date: date.toISOString(),
            events: dayEvents
          }
        })
      } catch (error) {
        console.error('Erreur lors du chargement des rendez-vous', error)
        toast.error('Erreur lors du chargement des rendez-vous')
      }
    },
    goToPreviousPeriod() {
      if (this.viewMode === 'employee-week') {
        // Move back one week
        const newDate = new Date(this.currentDate)
        newDate.setDate(this.currentDate.getDate() - 7)
        this.currentDate = newDate
      } else {
        // Move back one day
        const newDate = new Date(this.currentDate)
        newDate.setDate(this.currentDate.getDate() - 1)
        this.currentDate = newDate
      }
      this.fetchAppointments()
    },
    goToNextPeriod() {
      if (this.viewMode === 'employee-week') {
        // Move forward one week
        const newDate = new Date(this.currentDate)
        newDate.setDate(this.currentDate.getDate() + 7)
        this.currentDate = newDate
      } else {
        // Move forward one day
        const newDate = new Date(this.currentDate)
        newDate.setDate(this.currentDate.getDate() + 1)
        this.currentDate = newDate
      }
      this.fetchAppointments()
    },
    goToToday() {
      this.currentDate = new Date()
      this.fetchAppointments()
    },
    formatHour(hour) {
      return `${String(hour).padStart(2, '0')}:00`;
    },
    formatHourMinute(time) {
      if (!time) return '';
      if (typeof time === 'string' && time.includes(':')) {
        return time; // Already formatted "HH:MM"
      }
      const date = new Date(time);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    getEventStyle(event) {
      // Dynamic positioning offsets using start time & duration parameters
      return {
        top: `${event.topOffset || 40}px`,
        height: `${event.cardHeight || 90}px`
      };
    },
    handleCalendarClick(colIdx, hour) {
      console.log('Création rdv - Axe de colonne:', colIdx, 'Heure système:', hour);

      // Déterminer la date de la colonne cliquée
      let clickedDate;
      let employeeId = null;

      if (this.viewMode === 'employee-week') {
        // Mode semaine : chaque colonne est un jour
        clickedDate = new Date(this.activeColumns[colIdx].date);
        employeeId = this.selectedEmployeeIds[0] || null;
      } else {
        // Mode jour : toutes les colonnes sont le même jour
        clickedDate = new Date(this.currentDate);
        employeeId = this.activeColumns[colIdx]?.employeeId || null;
      }

      // Formatter la date au format YYYY-MM-DD
      const dateStr = clickedDate.toISOString().split('T')[0];

      // Formatter l'heure au format HH:00
      const timeStr = `${String(hour).padStart(2, '0')}:00`;

      console.log('📅 Navigation vers création:', { date: dateStr, time: timeStr, employeeId });

      // Rediriger vers la page de création avec les paramètres
      this.$router.push({
        path: '/appointments/new',
        query: {
          date: dateStr,
          time: timeStr,
          employeeId: employeeId
        }
      });
    },
    openSidebar(event) {
      this.selectedEvent = event;
      this.showSidebar = true;
    },
    closeSidebar() {
      this.showSidebar = false;
      this.selectedEvent = null;
    },
    startResize(mouseEvent, event) {
      this.isResizing = true;
      this.resizingEvent = event;
      this.originalHeight = event.cardHeight || 100;

      const startY = mouseEvent.clientY;

      const handleMouseMove = (e) => {
        const deltaY = e.clientY - startY;
        const newHeight = Math.max(50, this.originalHeight + deltaY); // Minimum 50px (30 minutes)

        // Calculer la nouvelle heure de fin
        const startDate = new Date(event.start);
        const startHour = startDate.getHours();
        const startMinute = startDate.getMinutes();
        const startTotalMinutes = startHour * 60 + startMinute;

        // Calculer la durée en minutes basée sur la nouvelle hauteur (100px = 1 heure = 60 minutes)
        const durationMinutes = Math.round((newHeight / 100) * 60);
        const endTotalMinutes = startTotalMinutes + durationMinutes;
        const endHour = Math.floor(endTotalMinutes / 60);
        const endMinute = endTotalMinutes % 60;

        // Update the event height
        event.cardHeight = newHeight;

        // Update the event end time
        const newEndDate = new Date(startDate);
        newEndDate.setHours(endHour, endMinute);
        event.end = newEndDate;

        // Show tooltip
        this.resizeTooltip.visible = true;
        this.resizeTooltip.x = e.clientX + 10;
        this.resizeTooltip.y = e.clientY + 10;
        this.resizeTooltip.previewEnd = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
      };

      const handleMouseUp = () => {
        this.isResizing = false;
        this.resizingEvent = null;
        this.resizeTooltip.visible = false;

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Ici on pourrait sauvegarder la nouvelle durée sur le backend
        console.log('Redimensionnement terminé:', event);
        console.log('Nouvelle durée:', this.formatHourMinute(event.start), '-', this.formatHourMinute(event.end));
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    formatEventTime(timeStr) {
      return timeStr; // Format: "HH:MM"
    },
    handleEditAppointment(event) {
      console.log('Modifier le rendez-vous:', event);
      // Rediriger vers la page de modification
      this.$router.push(`/appointments/edit/${event.id}`);
    },
    async handleCancelAppointment(event) {
      if (!confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) {
        return;
      }

      try {
        await api.appointments.updateAppointment(event.id, {
          status: 'Annulé'
        });

        toast.success('Rendez-vous annulé avec succès');
        this.closeSidebar();
        this.fetchAppointments(); // Rafraîchir la liste
      } catch (error) {
        console.error('Erreur lors de l\'annulation:', error);
        toast.error('Erreur lors de l\'annulation du rendez-vous');
      }
    },
    handleSendReminder(event) {
      console.log('Envoyer un rappel pour:', event);
      toast.info('Fonctionnalité de rappel à venir', { position: 'top-right' });
    },
    handlePayment(event) {
      console.log('Paiement pour le rendez-vous:', event);

      // Calculer le total des services
      const total = event.service.reduce((sum, s) => sum + (s.prix || 0), 0);

      // Préparer les données pour la caisse
      const paymentData = {
        appointmentId: event.id,
        clientId: event.clientId || null,
        clientName: event.nom,
        services: JSON.stringify(event.service),
        total: total.toFixed(2)
      };

      console.log('💰 Redirection vers la caisse avec:', paymentData);

      // Fermer le sidebar
      this.closeSidebar();

      // Rediriger vers la page de caisse avec les query params
      this.$router.push({
        path: '/caisse',
        query: paymentData
      });
    }
  }
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.day-column:first-child {
  border-left: none !important;
}
</style>